import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { safeShippingUrl } from "../../lib/shippingLinks";

export function shipmentParcelState(order) {
  const label = order.shipping_label || {};
  const intent = order.shipping_label_purchase_intent || {};
  const expected = order.shipping_packages?.length || 0;
  const ledger = intent.parcels || [];
  const labels = label.packages || [];
  const atIndex = (items, index) => items.some((p) => Number.isInteger(p.parcel_index))
    ? items.find((p) => p.parcel_index === index) || {}
    : items[index] || {};
  const parcels = Array.from({ length: Math.max(expected, ledger.length, labels.length, 1) }, (_, index) => {
    const entry = atIndex(ledger, index);
    const stored = atIndex(labels, index);
    const legacy = expected === 1 ? label : {};
    return { ...legacy, ...(entry.result || {}), ...stored, parcel_index: index,
      status: entry.status || stored.status || legacy.status || "unattempted",
      label_id: stored.label_id || entry.result?.label_id || legacy.label_id,
      tracking_number: stored.tracking_number || entry.result?.tracking_number || legacy.tracking_number,
      tracking_url: stored.tracking_url || entry.result?.tracking_url || legacy.tracking_url,
      label_download_url: stored.label_download_url || entry.result?.label_download?.href || legacy.label_download_url,
      expected_cost_minor: entry.expected_cost_minor,
    };
  });
  const purchased = (p) => ["purchased", "label_purchased"].includes(p.status) && p.label_id && p.tracking_number;
  const complete = order.status === "paid" && expected > 0 && parcels.length === expected && parcels.every(purchased)
    && label.status === "label_purchased" && order.shipping_status === "label_purchased";
  const uncertain = parcels.some((p) => ["purchasing", "uncertain", "reconciliation_required"].includes(p.status))
    || ["label_purchase_uncertain", "label_purchasing"].includes(order.shipping_status);
  const remaining = parcels.filter((p) => p.status === "unattempted");
  const remainingTotalMinor = remaining.every((p) => Number.isSafeInteger(p.expected_cost_minor) && p.expected_cost_minor > 0)
    ? remaining.reduce((sum, p) => sum + p.expected_cost_minor, 0) : null;
  const resumable = order.status === "paid" && order.shipping_status !== "shipped"
    && ledger.length === expected && remaining.length > 0 && !uncertain
    && Number.isSafeInteger(remainingTotalMinor) && remainingTotalMinor > 0
    && !(order.lines || []).some((line) => line.production_required && !["ready", "completed"].includes(line.production_status))
    && parcels.every((p) => p.status === "unattempted" || purchased(p));
  return { parcels, complete, uncertain, remaining, remainingTotalMinor, resumable, intent };
}

export default function AdminShipmentLabels({ order, busy = false, onRefresh, onMarkShipped, onVoid }) {
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState("");
  const state = shipmentParcelState(order);
  const disabled = busy || working;
  const refresh = async () => { if (onRefresh) await onRefresh(); };
  const reconcile = async () => {
    setWorking(true); setNotice("");
    try {
      await api.post(`/admin/orders/${order.id}/shipping/reconcile-labels`, { confirmation: true });
      setNotice("Provider records checked. No new postage was purchased.");
    } catch (error) { setNotice(formatApiError(error)); }
    finally { try { await refresh(); } finally { setWorking(false); } }
  };
  const resume = async () => {
    if (!state.resumable || !state.intent.idempotency_key || !state.intent.shipping_quote_id) return;
    const amount = ` for $${(state.remainingTotalMinor / 100).toFixed(2)}`;
    if (!window.confirm(`Buy postage for ${state.remaining.length} previously unattempted parcel(s)${amount}? This charges the shipping provider account. Purchased or uncertain parcels will not be bought again.`)) return;
    setWorking(true); setNotice("");
    try {
      await api.post(`/admin/orders/${order.id}/shipping/label`, {
        shipping_quote_id: state.intent.shipping_quote_id,
        idempotency_key: state.intent.idempotency_key,
        confirmation: true,
        resume_unattempted: true,
      });
      setNotice("Postage results updated. Verify every parcel before handing the order to the carrier.");
    } catch (error) { setNotice(formatApiError(error)); }
    finally { try { await refresh(); } finally { setWorking(false); } }
  };
  return (
    <div className="mt-3 space-y-3" data-testid="admin-shipment-labels">
      <p className="text-sm text-stone-300">{order.shipping_label?.carrier_name} {order.shipping_label?.service_name}</p>
      {!state.complete && order.shipping_status !== "shipped" && (
        <p role="alert" className="border-l-4 border-amber-400 bg-amber-950/20 p-3 text-sm text-amber-100">
          {state.uncertain ? "A postage purchase is uncertain or still in progress. Check provider records; do not buy it again." : "Postage is not complete for every parcel. Keep this order unshipped until all parcel labels are verified."}
        </p>
      )}
      {state.parcels.map((parcel) => (
        <div key={parcel.parcel_index} className="border-l-2 border-[#26C6D5] pl-3 text-sm text-stone-200" data-testid="shipment-parcel">
          <p>Parcel {parcel.parcel_index + 1} · {String(parcel.status).replaceAll("_", " ")}</p>
          {parcel.tracking_number && <p>Tracking: <span className="select-all">{parcel.tracking_number}</span></p>}
          {Number.isSafeInteger(parcel.actual_cost_minor) && <p>Postage: ${(parcel.actual_cost_minor / 100).toFixed(2)}</p>}
          <div className="mt-1 flex flex-wrap gap-3">
            {safeShippingUrl(parcel.label_download_url || parcel.label_download?.href) && <a className="underline" href={safeShippingUrl(parcel.label_download_url || parcel.label_download?.href)} target="_blank" rel="noopener noreferrer">Download parcel {parcel.parcel_index + 1} label</a>}
            {safeShippingUrl(parcel.tracking_url) && <a className="underline" href={safeShippingUrl(parcel.tracking_url)} target="_blank" rel="noopener noreferrer">Track parcel {parcel.parcel_index + 1}</a>}
          </div>
        </div>
      ))}
      {Number.isSafeInteger(order.shipping_actual_cost_minor) && <p className="text-sm text-stone-300">Recorded postage cost: ${(order.shipping_actual_cost_minor / 100).toFixed(2)}{!state.complete && order.shipping_status !== "shipped" ? " · incomplete or uncertain purchases may add costs" : ""}</p>}
      <div className="flex flex-wrap gap-2">
        {state.intent.idempotency_key && state.intent.parcels?.length > 0 && order.shipping_status !== "shipped" && <Button size="sm" variant="outline" disabled={disabled} onClick={reconcile}>Check provider records — no purchase</Button>}
        {state.resumable && <Button size="sm" disabled={disabled} onClick={resume}>Confirm postage for {state.remaining.length} remaining parcel(s)</Button>}
        {order.shipping_status !== "shipped" && <Button size="sm" disabled={disabled || !state.complete} onClick={onMarkShipped}>Mark all parcels shipped</Button>}
        {state.complete && state.parcels.length === 1 && <Button size="sm" variant="outline" disabled={disabled} onClick={onVoid}>Void unused label</Button>}
      </div>
      {state.parcels.length > 1 && <p className="text-xs text-stone-400">Multi-parcel refunds require individual provider review; no bulk void is available here.</p>}
      {notice && <p role="status" className="text-sm text-amber-100">{notice}</p>}
    </div>
  );
}
